// Top-level Settings page. Hosts the AccessibilityPanel today; future
// settings (notifications, integrations preferences, etc.) can be appended
// as additional cards in the same stacked-cards layout. We intentionally
// don't introduce a tab nav for a single tab — adds complexity without
// payoff (per the relocation task spec).

import React from 'react';
import { Accessibility as AccessibilityIcon, Sparkles } from 'lucide-react';
import AccessibilityPanel from '../components/Accessibility/AccessibilityPanel';
import { useWorkspace } from '../contexts/WorkspaceContext';

// Build-time gate — used to pick the build-correct localStorage prefix and
// to tweak the Reopen Setup Guide card's body copy. Both builds now ship a
// Setup Guide dock, so the card is no longer tree-shaken out of the Stripe
// build. Mirrors the gate pattern used elsewhere (Dashboard.tsx, Agents.tsx).
const IS_SHOPIFY_BUILD = import.meta.env.VITE_BILLING_PROVIDER === 'shopify';

// Constants kept in sync with `src/components/setup/SetupTour.tsx` (which
// owns the localStorage payload + listener contract). We DON'T import them
// from that module: SetupTour is React.lazy()-loaded in Layout.tsx and a
// static import here would pull it back into the eager bundle. The prefix is
// build-aware — SetupTour namespaces its localStorage key by build, so the
// reopen handler must clear the key that matches the current build:
//   Shopify build → prodagi_shopify_setup_<workspaceId>
//   App/main build → prodagi_setup_<workspaceId>
const SETUP_TOUR_LOCAL_STORAGE_PREFIX = IS_SHOPIFY_BUILD ? 'prodagi_shopify_setup_' : 'prodagi_setup_';
const OPEN_TOUR_EVENT = 'prodagi:open-setup-tour';

const Settings: React.FC = () => {
    const { activeWorkspace } = useWorkspace();

    // Clears the dismissed_at / completed_at markers from the workspace-
    // scoped setup-tour localStorage payload, resets step_index to 0 so the
    // tour restarts from Welcome (not mid-flow), then dispatches the same
    // CustomEvent the dock listener owns. The dock's `decide()` re-runs
    // after the listener fires and now sees no terminal marker → renders.
    const handleReopenTour = () => {
        const wsId = activeWorkspace?.id;
        if (!wsId) return;
        const key = `${SETUP_TOUR_LOCAL_STORAGE_PREFIX}${wsId}`;
        try {
            const raw = localStorage.getItem(key);
            const state = raw ? JSON.parse(raw) : {};
            delete state.dismissed_at;
            delete state.completed_at;
            state.step_index = 0;
            localStorage.setItem(key, JSON.stringify(state));
        } catch {
            // localStorage parse failure — drop the key entirely so the
            // dock falls into its fresh-state branch on next decide().
            try {
                localStorage.removeItem(key);
            } catch {
                // localStorage unavailable (private mode) — the
                // CustomEvent listener still force-opens the dock below.
            }
        }
        window.dispatchEvent(new CustomEvent(OPEN_TOUR_EVENT));
    };

    return (
        <div className="max-w-4xl mx-auto pb-8">
            <div className="mb-6">
                <h1 className="text-4xl font-bold gradient-text">Settings</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Personal preferences for this device. Workspace-level settings live in their respective pages.
                </p>
            </div>

            <div className="space-y-6">
                {/* Accessibility card */}
                <div className="card">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center flex-shrink-0">
                            <AccessibilityIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Accessibility</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Adjust font size, contrast, and motion preferences. These settings live on this device.
                            </p>
                        </div>
                    </div>
                    <div className="mt-6">
                        <AccessibilityPanel />
                    </div>
                </div>

                {/* Setup Guide card — renders on BOTH builds. The SetupTour
                    dock is build-aware (app + Shopify), so the reopen card
                    ships everywhere; only the prefix + body copy differ by
                    build. */}
                <div className="card">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Setup Guide</h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {IS_SHOPIFY_BUILD
                                    ? 'Walk through the Shopify storefront setup tour again — handy for re-watching the steps or recording a demo.'
                                    : 'Walk through the setup guide again — handy for re-watching the steps or recording a demo.'}
                            </p>
                        </div>
                    </div>
                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Restarts from the Welcome step. Your previous "Skip all" / "Finish" choice is cleared.
                        </p>
                        <button
                            type="button"
                            onClick={handleReopenTour}
                            disabled={!activeWorkspace}
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Sparkles className="w-4 h-4" />
                            Reopen Setup Guide
                        </button>
                    </div>
                </div>

                {/* Future settings cards drop in here. */}
            </div>
        </div>
    );
};

export default Settings;
